/**
 * cl-price-slider — dual-handle price range filter (two overlaid <input type="range">).
 *
 * Rebuild of the legacy theme's price slider, fitted to the new lookbook filter drawer.
 * - The two range inputs are UI only (no `name`); hidden fields carry the real form params.
 * - A hidden field is left BLANK when its handle is at the extreme, so cl-collection.js
 *   (which drops empty values) omits gte=0 / lte=max — a full range never counts as an
 *   "active" filter, and the native no-JS GET submit behaves the same.
 * - Custom element: auto-upgrades when cl-collection.js swaps the filter form via the
 *   Section Rendering API, so there is nothing to re-initialise after an AJAX apply.
 */
(function () {
  // Format a DOLLARS amount in the active presentment currency (Shopify.currency.active,
  // e.g. "USD"/"CAD" from content_for_header). Intl replaces the old Shopify.formatMoney
  // path, which never loaded here. currencyDisplay: keep in sync with the shop's CAD
  // money_format once a market exists — 'narrowSymbol' => "$10.00" (matches a bare-$ page),
  // 'symbol' => "CA$10.00", 'code' => "CAD 10.00".
  function formatMoney(dollars) {
    var code = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol'
      }).format(dollars);
    } catch (e) {
      return '$' + dollars.toFixed(2);
    }
  }

  // Must match the thumb width in cl-filters.liquid CSS (.filter-price__input thumb).
  var THUMB = 20;

  // A native range thumb's CENTRE travels from THUMB/2 to (track − THUMB/2), NOT 0%→100%.
  // Map the fraction (0..1) across (100% − THUMB) and add half a thumb so an overlaid
  // element (label / fill edge) sits exactly on the thumb centre at every position.
  function thumbPos(frac) {
    return 'calc(' + frac + ' * (100% - ' + THUMB + 'px) + ' + THUMB / 2 + 'px)';
  }

  class ClPriceSlider extends HTMLElement {
    connectedCallback() {
      if (this._wired) return; // guard against a double upgrade
      this.minInput = this.querySelector('[data-price-min]');
      this.maxInput = this.querySelector('[data-price-max]');
      if (!this.minInput || !this.maxInput) return;
      this._wired = true;

      this.fill = this.querySelector('[data-price-fill]');
      this.minLabel = this.querySelector('[data-price-min-label]');
      this.maxLabel = this.querySelector('[data-price-max-label]');
      this.minHidden = this.querySelector('[data-price-min-hidden]');
      this.maxHidden = this.querySelector('[data-price-max-hidden]');

      this.floor = parseFloat(this.minInput.min);
      this.ceil = parseFloat(this.minInput.max);

      this._onInput = this.onInput.bind(this);
      this.minInput.addEventListener('input', this._onInput);
      this.maxInput.addEventListener('input', this._onInput);

      // Grab feedback (scale the thumb) via Pointer Events — works for mouse, touch and pen
      // alike, unlike :hover/:active which are unreliable on touch (esp. iOS during a drag).
      this._onGrab = function (e) { e.currentTarget.classList.add('is-grabbing'); };
      this._onRelease = function () {
        this.minInput.classList.remove('is-grabbing');
        this.maxInput.classList.remove('is-grabbing');
      }.bind(this);
      this.minInput.addEventListener('pointerdown', this._onGrab);
      this.maxInput.addEventListener('pointerdown', this._onGrab);
      // Release can land anywhere (you can drag past the thumb), so listen on document.
      document.addEventListener('pointerup', this._onRelease);
      document.addEventListener('pointercancel', this._onRelease);

      this.render();
    }

    disconnectedCallback() {
      // The element is replaced on every AJAX filter apply — drop the document-level
      // listeners so they don't pile up and leak references to dead inputs.
      if (this._onRelease) {
        document.removeEventListener('pointerup', this._onRelease);
        document.removeEventListener('pointercancel', this._onRelease);
      }
    }

    onInput(e) {
      var minV = parseFloat(this.minInput.value);
      var maxV = parseFloat(this.maxInput.value);
      // Stop the handles crossing: clamp whichever one is being dragged to the other.
      if (e.target === this.minInput && minV > maxV) this.minInput.value = maxV;
      if (e.target === this.maxInput && maxV < minV) this.maxInput.value = minV;
      this.render();
    }

    render() {
      var minV = parseFloat(this.minInput.value);
      var maxV = parseFloat(this.maxInput.value);
      var span = this.ceil - this.floor || 1;
      var fracMin = (minV - this.floor) / span; // 0..1
      var fracMax = (maxV - this.floor) / span;

      if (this.fill) {
        // Span between the two thumb centres. The +THUMB/2 offset cancels in the
        // difference, so the width is just the fraction-gap across (100% − THUMB).
        this.fill.style.left = thumbPos(fracMin);
        this.fill.style.width = 'calc(' + (fracMax - fracMin) + ' * (100% - ' + THUMB + 'px))';
      }
      if (this.minLabel) {
        this.minLabel.textContent = formatMoney(minV);
        this.minLabel.style.left = thumbPos(fracMin);
      }
      if (this.maxLabel) {
        this.maxLabel.textContent = formatMoney(maxV);
        this.maxLabel.style.left = thumbPos(fracMax);
      }
      // Hidden form values — blank at the extreme so the param is dropped on submit.
      if (this.minHidden) this.minHidden.value = minV > this.floor ? minV : '';
      if (this.maxHidden) this.maxHidden.value = maxV < this.ceil ? maxV : '';
    }
  }

  if (!customElements.get('cl-price-slider')) {
    customElements.define('cl-price-slider', ClPriceSlider);
  }
})();
